/**
 * F1b — IndexedDB-backed local-edit queue.
 *
 * When a WRITE_POLICY operation exhausts its retry budget, the wrapper
 * dispatches the pending write here instead of throwing (per
 * `offline-and-resilience.md`). A drain worker (see
 * `useLocalEditQueueDrain`) replays the queue on `online`, window focus,
 * and a 60-second tick.
 *
 * Leaf module — no React, no Convex imports. Browser-only (uses
 * `globalThis.indexedDB`); SSR / Node tests must provide a polyfill
 * (`fake-indexeddb/auto`).
 *
 * Memory: feedback_graph_retry_policy.md (this is the F1b half of F1).
 *
 * @see {@link ../../writes/lanes.md} — this module is **lane A** in the
 * writes-routing vocabulary. Other lanes: B (`vault/saveProvider.ts`),
 * C (`nipe/pendingWriteBodies.ts`).
 */

export type PendingKind = 'save-back' | 'create-link' | 'invite';

export type GraphErrorKind =
  | 'conflict'
  | 'access-denied'
  | 'transient'
  | 'fatal';

export interface PendingTargetRef {
  driveId: string;
  itemId: string;
  siteId?: string;
}

export interface PendingSharePointWrite {
  id: string;
  kind: PendingKind;
  targetRef: PendingTargetRef;
  /** Op-specific body. Keep it primitive-only so structured-clone works. */
  payload: unknown;
  baseEtag?: string;
  attempts: number;
  lastErrorKind?: GraphErrorKind;
  lastErrorMessage?: string;
  createdAt: number;
  nextAttemptAt: number;
  /** Idempotency-Key for Graph; UUID, generated at enqueue time. */
  idempotencyKey: string;
}

const DB_NAME = 'repancy.localEditQueue';
const DB_VERSION = 1;
const STORE = 'pendingWrites';
const ABANDON_AFTER_ATTEMPTS = 10;

function uuid(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  // Fallback for old environments / test harnesses w/o crypto.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function getIDB(): IDBFactory {
  const idb = globalThis.indexedDB;
  if (!idb) {
    throw new Error('localEditQueue: indexedDB not available (SSR or unpolyfilled test env)');
  }
  return idb;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = getIDB().open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('by_nextAttemptAt', 'nextAttemptAt');
        store.createIndex('by_kind', 'kind');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Run `fn` inside a single IDB transaction and resolve with its return value
 * once the transaction commits. IDB transactions auto-close when no request
 * is in flight, so `fn` MUST chain its IDBRequests synchronously
 * (e.g. via callbacks); using `await` between requests breaks the txn.
 *
 * `fn` should call `done(value)` once it has captured the value to return.
 */
function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore, done: (value: T) => void) => void,
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const store = tx.objectStore(STORE);
        let captured: T | undefined;
        let hasValue = false;
        const done = (value: T) => {
          captured = value;
          hasValue = true;
        };
        try {
          fn(store, done);
        } catch (err) {
          tx.abort();
          db.close();
          reject(err);
          return;
        }
        tx.oncomplete = () => {
          db.close();
          if (hasValue) resolve(captured as T);
          else resolve(undefined as unknown as T);
        };
        tx.onerror = () => {
          db.close();
          reject(tx.error);
        };
        tx.onabort = () => {
          db.close();
          reject(tx.error ?? new Error('transaction aborted'));
        };
      }),
  );
}

export interface EnqueueInput {
  kind: PendingKind;
  targetRef: PendingTargetRef;
  payload: unknown;
  baseEtag?: string;
}

export async function enqueue(input: EnqueueInput): Promise<PendingSharePointWrite> {
  const now = Date.now();
  const row: PendingSharePointWrite = {
    id: uuid(),
    kind: input.kind,
    targetRef: input.targetRef,
    payload: input.payload,
    baseEtag: input.baseEtag,
    attempts: 0,
    createdAt: now,
    nextAttemptAt: now,
    idempotencyKey: uuid(),
  };
  await withStore<void>('readwrite', (store, done) => {
    const req = store.add(row);
    req.onsuccess = () => done(undefined);
  });
  return row;
}

export async function listAll(): Promise<PendingSharePointWrite[]> {
  return withStore<PendingSharePointWrite[]>('readonly', (store, done) => {
    const req = store.getAll() as IDBRequest<PendingSharePointWrite[]>;
    req.onsuccess = () => done(req.result ?? []);
  });
}

export async function listDue(now: number = Date.now()): Promise<PendingSharePointWrite[]> {
  const all = await listAll();
  return all.filter((r) => r.nextAttemptAt <= now);
}

export async function get(id: string): Promise<PendingSharePointWrite | undefined> {
  return withStore<PendingSharePointWrite | undefined>('readonly', (store, done) => {
    const req = store.get(id) as IDBRequest<PendingSharePointWrite | undefined>;
    req.onsuccess = () => done(req.result);
  });
}

export async function remove(id: string): Promise<void> {
  await withStore<void>('readwrite', (store, done) => {
    const req = store.delete(id);
    req.onsuccess = () => done(undefined);
  });
}

export interface RecordAttemptInput {
  id: string;
  errorKind: GraphErrorKind;
  errorMessage: string;
  /** Optional override for the next attempt time. Defaults to 250ms × 2^attempts + jitter. */
  nextAttemptAt?: number;
}

export async function recordAttempt(
  input: RecordAttemptInput,
): Promise<{ kept: PendingSharePointWrite } | { abandoned: PendingSharePointWrite }> {
  type Result = { kept: PendingSharePointWrite } | { abandoned: PendingSharePointWrite };
  return withStore<Result>('readwrite', (store, done) => {
    const getReq = store.get(input.id) as IDBRequest<PendingSharePointWrite | undefined>;
    getReq.onsuccess = () => {
      const row = getReq.result;
      if (!row) {
        throw new Error(`localEditQueue: no pending row with id ${input.id}`);
      }
      row.attempts++;
      row.lastErrorKind = input.errorKind;
      row.lastErrorMessage = input.errorMessage;
      if (row.attempts >= ABANDON_AFTER_ATTEMPTS) {
        const delReq = store.delete(input.id);
        delReq.onsuccess = () => done({ abandoned: row });
        return;
      }
      const base = Math.min(250 * 2 ** row.attempts, 30_000);
      const jitter = Math.random() * base;
      row.nextAttemptAt = input.nextAttemptAt ?? Date.now() + Math.min(base + jitter, 30_000);
      const putReq = store.put(row);
      putReq.onsuccess = () => done({ kept: row });
    };
  });
}

export async function clearAll(): Promise<void> {
  await withStore<void>('readwrite', (store, done) => {
    const req = store.clear();
    req.onsuccess = () => done(undefined);
  });
}

/** Test-only: drop the DB so the next open re-runs onupgradeneeded. */
export async function __dropDbForTests(): Promise<void> {
  const idb = getIDB();
  return new Promise((resolve, reject) => {
    const req = idb.deleteDatabase(DB_NAME);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
    req.onblocked = () => resolve();
  });
}

export const __INTERNAL = { ABANDON_AFTER_ATTEMPTS };
